import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "./utils/cn";

/**
 * Badge — status pill. Map status → color via `variant`.
 */
export function Badge({
  variant = "neutral",
  size = "md",
  className,
  children,
}: {
  variant?: "neutral" | "success" | "warn" | "danger" | "accent" | "info";
  size?: "sm" | "md";
  className?: string;
  children: ReactNode;
}) {
  const tint: Record<string, string> = {
    neutral: "border-[var(--nx-line-strong)] text-[var(--nx-text-dim)] bg-[var(--nx-surface-2)]",
    success: "border-[color:rgba(52,211,153,0.3)] text-[var(--nx-success)] bg-[rgba(52,211,153,0.08)]",
    warn: "border-[color:rgba(245,158,11,0.3)] text-[var(--nx-warn)] bg-[rgba(245,158,11,0.08)]",
    danger: "border-[color:rgba(244,63,94,0.3)] text-[var(--nx-danger)] bg-[rgba(244,63,94,0.08)]",
    accent: "border-[color:rgba(107,79,230,0.3)] text-[var(--nx-accent)] bg-[var(--nx-accent-soft)]",
    info: "border-[color:rgba(34,211,238,0.3)] text-[var(--nx-info)] bg-[rgba(34,211,238,0.08)]",
  };
  const sz = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium uppercase tracking-[0.14em]",
        tint[variant],
        sz,
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Pill — filter pill with optional onRemove. */
export function Pill({
  onRemove,
  className,
  children,
}: {
  onRemove?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-[var(--nx-line)] bg-[var(--nx-surface-2)] px-3 py-1 text-xs text-[var(--nx-text-dim)]",
        className,
      )}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-full p-0.5 hover:bg-[var(--nx-surface-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nx-accent)]"
          aria-label="Remove"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
            <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : null}
    </span>
  );
}

/** Kbd — renders a <kbd> with consistent styling. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border border-[var(--nx-line-strong)] bg-[var(--nx-surface-2)] px-1.5 font-mono text-[10px] text-[var(--nx-text-dim)]",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

/** Divider — horizontal or vertical, optional label. */
export function Divider({
  orientation = "horizontal",
  label,
  className,
}: {
  orientation?: "horizontal" | "vertical";
  label?: string;
  className?: string;
}) {
  if (orientation === "vertical") {
    return <span className={cn("inline-block h-full w-px bg-[var(--nx-line)]", className)} aria-hidden />;
  }
  if (label) {
    return (
      <div className={cn("flex items-center gap-3", className)}>
        <span className="h-px flex-1 bg-[var(--nx-line)]" />
        <span className="text-xs uppercase tracking-[0.18em] text-[var(--nx-text-faint)]">{label}</span>
        <span className="h-px flex-1 bg-[var(--nx-line)]" />
      </div>
    );
  }
  return <div className={cn("h-px w-full bg-[var(--nx-line)]", className)} role="separator" />;
}

/** Card — base surface card. Interactive variant adds hover. */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { interactive?: boolean; highlight?: boolean }>(
  ({ className, interactive, highlight, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-[16px] border border-[var(--nx-line)] bg-[var(--nx-surface)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]",
        interactive
          ? "transition-[border-color,transform] duration-[320ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-[var(--nx-line-strong)]"
          : undefined,
        highlight ? "nx-card-highlight" : undefined,
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";
